<template>
    <div class="searchable-language-list">
        <div class="search-bar">
            <input
                v-model="keyword"
                type="text"
                class="search-input"
                :placeholder="searchPlaceholder"
                :aria-label="searchPlaceholder"
            >
        </div>
        <div class="language-list">
            <div
                v-for="lang in filteredLanguages"
                :key="lang.locale"
                class="language-item"
                :class="{ active: lang.locale === selectedLocale }"
                @click="handleItemOnClick(lang.locale)"
            >
                {{ lang.title }}
            </div>
            <div v-if="filteredLanguages.length === 0" class="language-list-empty">
                {{ emptyText }}
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue'
import { LanguageItem } from '@shared-data'

export default defineComponent({
    name: 'SearchableLanguageList',
    props: {
        /**
         * 供選擇的語言清單
         */
        languages: {
            type: Array as PropType<LanguageItem[]>,
            required: true,
        },
        /**
         * 目前選中的語言 locale，用於高亮顯示
         */
        selectedLocale: {
            type: String,
            default: '',
        },
        /**
         * 搜尋框的 placeholder 文字
         */
        searchPlaceholder: {
            type: String,
            default: '',
        },
        /**
         * 搜尋無符合結果時顯示的文字
         */
        emptyText: {
            type: String,
            default: '',
        },
    },
    emits: ['select'],
    setup(props, { emit }) {
        // 搜尋關鍵字
        const keyword = ref('')

        /**
         * 依關鍵字過濾後的語言清單，比對原生名稱、英文名稱與 locale 代碼，忽略大小寫
         * @return {LanguageItem[]}
         */
        const filteredLanguages = computed<LanguageItem[]>(() => {
            // 去除頭尾空白並轉小寫，作為比對基準
            const normalizedKeyword = keyword.value.trim().toLowerCase()

            // 沒有輸入關鍵字時回傳完整清單
            if (normalizedKeyword === '') {
                return props.languages
            }

            // 過濾出原生名稱、英文名稱或 locale 代碼符合關鍵字的語言
            return props.languages.filter((lang) => (
                lang.title.toLowerCase().includes(normalizedKeyword) ||
                lang.englishTitle.toLowerCase().includes(normalizedKeyword) ||
                lang.locale.toLowerCase().includes(normalizedKeyword)
            ))
        })

        /**
         * 點擊語言項目
         * @param locale {string} 選擇的語言 locale
         * @return {void}
         */
        const handleItemOnClick = (locale: string): void => {
            // 將選取的 locale 交給父層處理，本元件不做任何 store / 導頁邏輯
            emit('select', locale)
        }

        return {
            keyword,
            filteredLanguages,
            handleItemOnClick,
        }
    },
})
</script>
<style scoped lang="scss">
// 語言項目文字顏色
$language-item-color: var(--ui-grey-darkest);
// 選中語言項目的背景色
$language-item-active-bg-color: var(--ui-ignite);
// 選中語言項目的文字顏色
$language-item-active-color: #fff;
// 搜尋框邊框顏色
$search-input-border-color: #e5e5e5;

.searchable-language-list {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    // 沒有被外層限制高度時的安全上限，父層可透過 --searchable-language-list-max-height 覆蓋
    // 同時保留 vh fallback，dvh 支援時覆蓋前者
    max-height: var(--searchable-language-list-max-height, 70vh);
    max-height: var(--searchable-language-list-max-height, 70dvh);

    .search-bar {
        flex-shrink: 0;
        padding-bottom: 12px;

        .search-input {
            width: 100%;
            height: 44px;
            padding: 0 16px;
            box-sizing: border-box;
            border: 1px solid $search-input-border-color;
            border-radius: 10px;
            font-size: 16px;
            color: $language-item-color;
            outline: none;

            &:focus {
                border-color: var(--ui-ignite);
            }
        }
    }

    .language-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        touch-action: pan-y pinch-zoom;
        padding-bottom: env(safe-area-inset-bottom);

        .language-item {
            display: flex;
            align-items: center;
            padding: 14px 16px;
            font-size: 16px;
            font-weight: 500;
            color: $language-item-color;
            border-radius: 10px;
            cursor: pointer;
            transition: background-color 0.2s ease, color 0.2s ease;

            &:hover {
                background-color: rgba(0, 0, 0, 0.05);
            }

            &.active {
                background-color: $language-item-active-bg-color;
                color: $language-item-active-color;
            }
        }

        .language-list-empty {
            padding: 24px 16px;
            text-align: center;
            font-size: 14px;
            color: rgba(0, 0, 0, 0.45);
        }
    }
}
</style>
